<template>
  <div>
    <!-- Header & Actions -->
    <div class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-800 flex items-center gap-2">
          <Link href="/devolutions" class="text-gray-400 hover:text-orange-500 transition-colors font-bold">
            Devoluciones
          </Link>
          <span class="text-gray-300 font-light">/</span>
          <span>Devolución #{{ devolution.id }}</span>
        </h1>
        <p class="text-sm text-gray-500 mt-0.5">Orden #{{ devolution.order.number_order }}</p>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <button
          v-if="devolution.status === 'pending'"
          class="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm"
          @click="approveDevolution"
        >
          <i class="fa fa-check-circle" />
          Aprobar Devolución
        </button>
      </div>
    </div>


    <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
      <div class="p-8">
        <div class="flex justify-between items-center mb-6">
          <div>
            <h2 class="text-xl font-semibold">Devolución #{{ devolution.id }}</h2>
            <p class="text-gray-600">Orden #{{ devolution.order.number_order }}</p>
          </div>
          <div class="text-right">
            <div class="text-sm text-gray-600">Estado</div>
            <span :class="getStatusClass(devolution.status)" class="font-semibold">
              {{ getStatusText(devolution.status) }}
            </span>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-6 mb-6">
          <div>
            <div class="text-sm text-gray-600">Fecha</div>
            <div class="font-semibold">{{ getDate(devolution.created_at) }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-600">Tipo de Devolución</div>
            <div class="font-semibold">
              {{ devolution.return_type === 'partial' ? 'Parcial' : 'Completa' }}
            </div>
          </div>
        </div>

        <div class="mb-6">
          <h3 class="text-lg font-medium mb-4">Productos Devueltos</h3>
          <div class="space-y-4">
            <div v-for="product in devolution.devolutionProducts" :key="product.id" class="flex items-center space-x-4">
              <div class="flex-1">
                <div class="font-medium">{{ product.orderProduct.name }}</div>
                <div class="text-sm text-gray-600">
                  Cantidad original: {{ product.orderProduct.quantity }}
                </div>
              </div>
              <div class="text-right">
                <div class="font-medium">{{ product.quantity }} unidades</div>
                <div class="text-sm text-gray-600">
                  {{ formatCurrency(product.amount) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mb-6">
          <h3 class="text-lg font-medium mb-4">Información de Reembolso</h3>
          <div class="grid grid-cols-2 gap-6">
            <div>
              <div class="text-sm text-gray-600">Método de Reembolso</div>
              <div class="font-semibold">
                {{ getRefundMethodText(devolution.refund_method) }}
              </div>
            </div>
            <div v-if="devolution.refund_reference">
              <div class="text-sm text-gray-600">Referencia</div>
              <div class="font-semibold">{{ devolution.refund_reference }}</div>
            </div>
          </div>
        </div>

        <div class="mb-6">
          <h3 class="text-lg font-medium mb-4">Resumen</h3>
          <div class="grid grid-cols-2 gap-6">
            <div>
              <div class="text-sm text-gray-600">Monto Total</div>
              <div class="font-semibold text-lg">{{ formatCurrency(devolution.total_amount) }}</div>
            </div>
            <div>
              <div class="text-sm text-gray-600">Balance de la Orden</div>
              <div class="font-semibold text-lg">{{ formatCurrency(devolution.order.balance) }}</div>
            </div>
          </div>
        </div>

        <div v-if="devolution.notes" class="mb-6">
          <h3 class="text-lg font-medium mb-4">Notas</h3>
          <div class="bg-gray-50 p-4 rounded">
            {{ devolution.notes }}
          </div>
        </div>
      </div>

      <div class="flex items-center justify-end px-8 py-4 bg-gray-50 border-t border-gray-100">
        <Link class="btn-gray" href="/devolutions">Volver</Link>
        <button
          v-if="devolution.status === 'pending'"
          class="btn-blue ml-3"
          @click="approveDevolution"
        >
          Aprobar Devolución
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'

export default {
  components: {
    Head,
    Link,
  },
  layout: Layout,
  props: {
    devolution: Object,
  },
  methods: {
    getDate(date) {
      return new Date(date).toLocaleString('es-VE', { hour12: false })
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('es-VE', {
        style: 'currency',
        currency: 'VES',
      }).format(amount)
    },
    getStatusClass(status) {
      return {
        'pending': 'text-yellow-600',
        'approved': 'text-green-600',
        'rejected': 'text-red-600',
      }[status]
    },
    getStatusText(status) {
      return {
        'pending': 'Pendiente',
        'approved': 'Aprobada',
        'rejected': 'Rechazada',
      }[status]
    },
    getRefundMethodText(method) {
      return {
        'cash': 'Efectivo',
        'transfer': 'Transferencia',
        'credit_note': 'Nota de Crédito',
        '': 'Sin reembolso',
      }[method]
    },
    approveDevolution() {
      if (confirm('¿Está seguro de aprobar esta devolución?')) {
        this.$inertia.post(`/devolutions/${this.devolution.id}/approve`)
      }
    },
  },
}
</script>
